import { ReactNode, useContext, useEffect } from 'react';
import { MutationFunction, useMutation } from '@tanstack/react-query';
import { UndoAction } from 'assets/js/pages/contacts/data-hygiene/UndoAction';
import { TableContext, TableRowContext } from 'components/table/TableContext';
import { queryClient } from 'queries/queryClient';
import { GivebutterAxiosError } from 'types/error';

interface TableRowSuccessStateProps {
    successMessage?: string;
    undoMutationFn?: MutationFunction<unknown, void>;
    invalidationKeys?: (string | number)[][];
    children?: ReactNode;
}

const TableRowSuccessState = ({
    successMessage = 'Saved Successfully',
    undoMutationFn,
    invalidationKeys,
    children,
}: TableRowSuccessStateProps) => {
    const { table } = useContext(TableContext);
    const { row } = useContext(TableRowContext);
    useEffect(() => {
        const timer = setTimeout(() => {
            invalidationKeys?.forEach(key => {
                // NOTE: will remove success state component from table
                queryClient.invalidateQueries({
                    queryKey: key,
                });
            });
        }, 3000);

        return () => clearTimeout(timer);
    });

    const {
        mutate: undoUpdate,
        isPending: undoIsPending,
        isError: undoIsError,
        error: undoError,
        isSuccess: undoIsSuccess,
    } = useMutation({
        mutationFn: undoMutationFn,
        onMutate: () => {
            if (table.options.enableRowPending) {
                table.removeRowPending(row.id);
            }
        },
        onSuccess: data => {
            if (table.options.enableRowReplacement) {
                table.removeRowReplacement(row.id);
            }

            // IMPORTANT: don't invalidate queries using invalidationKeys here,
            // those are for the true success state of the original action (if undo timeout is reached)
        },
        onError: (error: GivebutterAxiosError) => {},
    });

    return (
        <div className="flex w-full items-center justify-between gap-4">
            <span className="text-sm font-medium text-gray-700">{successMessage}</span>
            <div className="ml-auto flex justify-end gap-2">
                <UndoAction
                    undoFn={undoUpdate}
                    undoIsPending={undoIsPending || undoIsSuccess}
                    undoIsError={undoIsError}
                    undoError={undoError}
                />

                {children}
            </div>
        </div>
    );
};
export { TableRowSuccessState };
